import React, { useMemo, useState, useEffect, useRef } from 'react';
import Navbar from '../../components/NavBar2/Container';
import * as S from './styles';
import { IPresenterProps } from './types';
import { IBusinessImage } from '../../state/api/types';
import { Column, Row } from '../../components/CustomStyledComponents/StyledComponents';
import { ReactComponent as RadioBtnNO } from '../../assets/img/radioButton/radioBtnNO.svg';
import { ReactComponent as RadioBtnYES } from '../../assets/img/radioButton/radioBtnYES.svg';
import COLORS from '../../utils/Colors';
import { ReactComponent as Star } from '../../assets/img/star.svg';
import BookingAuthModal from '../../components/BookingAuthModal';
import ItemPill from '../../components/ItemPill/Container';
import ReviewProgressItem from '../../components/ReviewProgressItem/Container';
import CommentsSection from '../../components/CommentsSection/Container';
import BusinessNotFound from '../../components/BusinessNotFound';
import { calculateAverageStarRating, calculatePercentageStarCount, calculateTotalStartsFromStarCount, isVariableService, formatServicePrice } from '../../utils/helpers';

// Lazy load BookingFlow and RequestForm for better performance
const BookingFlow = React.lazy(() => import('../../components/BookingFlow/Container'));
const RequestForm = React.lazy(() => import('../../components/RequestForm/Container'));

// Lightbox component for image gallery
const Lightbox = ({ images, initialIndex, onClose }: {
    images: IBusinessImage[];
    initialIndex: number;
    onClose: () => void;
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const trackRef = useRef<HTMLDivElement>(null);

    // Transform slide
    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
        }
    }, [currentIndex]);

    // Keyboard + scroll lock
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(currentIndex - 1);
            if (e.key === 'ArrowRight' && currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKey);

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKey);
        };
    }, [currentIndex, onClose, images.length]);

    return (
        <S.LightboxOverlay onClick={onClose}>
            <S.LightboxHeader>
                <span>{currentIndex + 1} / {images.length}</span>
                <S.CloseButton onClick={onClose}>×</S.CloseButton>
            </S.LightboxHeader>

            <S.LightboxBody onClick={(e) => e.stopPropagation()}>
                {currentIndex > 0 && (
                    <S.ArrowButton left onClick={() => setCurrentIndex(currentIndex - 1)}>‹</S.ArrowButton>
                )}

                <S.CarouselTrack ref={trackRef}>
                    {images.map((img, idx) => (
                        <S.CarouselSlide key={img.imageUrl}>
                            <img src={img.imageUrl} alt={`Image ${idx + 1}`} />
                        </S.CarouselSlide>
                    ))}
                </S.CarouselTrack>

                {currentIndex < images.length - 1 && (
                    <S.ArrowButton right onClick={() => setCurrentIndex(currentIndex + 1)}>›</S.ArrowButton>
                )}
            </S.LightboxBody>
        </S.LightboxOverlay>
    );
};

// Feature flags
const SHOW_REVIEWS = false;

// Helper to detect tattoo shops
const isTattooShop = (business?: { businessType?: string | { id?: string; name?: string } }): boolean => {
    if (!business?.businessType) return false;
    const typeName = typeof business.businessType === 'object'
        ? business.businessType.name
        : business.businessType;
    return typeName?.toLowerCase() === 'tattoo' || typeName?.toLowerCase() === 'tatuajes';
};

const BusinessDetailPresenter = (props: IPresenterProps) => {
    const isTattoo = isTattooShop(props.selectedBusiness);

    // Memoize expensive calculations
    const totalReviews = useMemo(
        () => props.selectedBusiness?.reviewComments?.length ?? 0,
        [props.selectedBusiness?.reviewComments]
    );

    const totalStarCount = useMemo(
        () => calculateTotalStartsFromStarCount(props.selectedBusiness?.starCount),
        [props.selectedBusiness?.starCount]
    );

    // Image gallery lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    if (props.isLoaderVisible) {
        return (
            <>
                <div style={{ textAlign: 'center' }}>
                    <S.LoaderContainer>
                        <S.Spinner />
                    </S.LoaderContainer>
                </div>
                <div style={{ marginTop: 16 }}>
                    <p style={{ textAlign: 'center', color: COLORS.hsl000100099, fontSize: 16 }}>Cargando información del negocio...</p>
                </div>
            </>
        )
    }

    // Show error state if there's an API error or business not found
    if (props.apiError || props.isBusinessNotFound || !props.selectedBusiness) {
        return (
            <S.PageContainer>
                <Navbar isSearchBarHidden={true} navWidth={80} />
                <BusinessNotFound
                    reason={props.apiError || props.notFoundReason}
                    businessName={props.selectedBusiness?.name}
                    onRetry={props.apiError ? props.retryLoadingBusinesses : undefined}
                    isApiError={!!props.apiError}
                />
            </S.PageContainer>
        );
    }

    return (
        <>
            {/* Always render Navbar */}
            <Navbar isSearchBarHidden={true} navWidth={80} />

            {/* Render BookingFlow when visible */}
            {props.isBookingFlowVisible && props.selectedBusiness && (
                <React.Suspense fallback={<S.LoaderContainer><S.Spinner /></S.LoaderContainer>}>
                    <BookingFlow
                        selectedBusiness={props.selectedBusiness}
                        bookings={props.bookings}
                        onClose={props.toggleBookingFlow}
                    />
                </React.Suspense>
            )}

            {/* Render RequestForm when visible (for VARIABLE services) */}
            {props.isRequestFormVisible && props.selectedBusiness && (
                <React.Suspense fallback={<S.LoaderContainer><S.Spinner /></S.LoaderContainer>}>
                    <RequestForm
                        selectedBusiness={props.selectedBusiness}
                        preSelectedServices={props.selectedServices}
                        onClose={props.toggleRequestForm}
                    />
                </React.Suspense>
            )}

            {/* Render Business Detail when neither BookingFlow nor RequestForm is visible */}
            {!props.isBookingFlowVisible && !props.isRequestFormVisible && (
                <S.PageContainer>

                    <BookingAuthModal
                        isVisible={props.showAuthModal}
                        onClose={() => props.setShowAuthModal(false)}
                    />

                    <S.Container>
                <S.Content>
                    <S.FirstRow id='firstRow'>
                        <S.Title>
                            {props.selectedBusiness?.name}
                        </S.Title>
                    </S.FirstRow>

                    <S.SecondRow>
                        <div>
                            <S.LocationInfo>{props.selectedBusiness?.fullAddress?.address1}, {props.selectedBusiness?.fullAddress?.city}</S.LocationInfo>
                        </div>
                    </S.SecondRow>

                    {/* Hero Gallery or Gradient Header */}
                    {(() => {
                        const images = props.selectedBusiness?.images;
                        const hasImages = images && images.length > 0;

                        if (hasImages) {
                            const visibleImages = images.slice(0, 5);

                            return (
                                <>
                                    <S.ImageGrid count={images.length}>
                                        {visibleImages.map((img, idx) => (
                                            <S.GridImage
                                                key={img.imageUrl}
                                                onClick={() => {
                                                    setLightboxIndex(idx);
                                                    setLightboxOpen(true);
                                                }}
                                            >
                                                <img src={img.imageUrl} alt={`Image ${idx + 1}`} />

                                                {idx === 4 && images.length > 5 && (
                                                    <S.ShowAllButton onClick={(e) => {
                                                        e.stopPropagation();
                                                        setLightboxIndex(idx);
                                                        setLightboxOpen(true);
                                                    }}>
                                                        Ver todas las fotos ({images.length})
                                                    </S.ShowAllButton>
                                                )}
                                            </S.GridImage>
                                        ))}
                                    </S.ImageGrid>

                                    {lightboxOpen && (
                                        <Lightbox
                                            images={images}
                                            initialIndex={lightboxIndex}
                                            onClose={() => setLightboxOpen(false)}
                                        />
                                    )}
                                </>
                            );
                        }

                        // Keep existing gradient fallback
                        return (
                            <S.GradientHeader>
                                <S.BusinessInitialCircle>
                                    {props.selectedBusiness?.name?.charAt(0).toUpperCase()}
                                </S.BusinessInitialCircle>
                                <S.GradientTitle>{props.selectedBusiness?.name}</S.GradientTitle>
                                <S.GradientSubtitle>
                                    <Star height="16px" width="16px" />
                                    <span>•</span>
                                    <span>{props.selectedBusiness?.fullAddress?.city}</span>
                                </S.GradientSubtitle>
                            </S.GradientHeader>
                        );
                    })()}

                    {isTattoo ? (
                        /* Tattoo shop layout: prominent CTA only */
                        <S.TattooCTASection>
                            <S.TattooCTAButton onClick={props.toggleRequestForm}>
                                Reserva tu cita
                            </S.TattooCTAButton>
                        </S.TattooCTASection>
                    ) : (
                        /* Regular business: services list + booking sidebar */
                        <S.BottomSection>
                            <S.ServicesContainer>
                                <S.SectionHeader>Servicios</S.SectionHeader>
                                {props.selectedBusiness?.services?.map(service => (
                                    <S.ServiceItem key={service.id}
                                        onClick={() => props.toggleSelectedService(service)} isSelected={service.isSelected || false}>
                                        <Column>
                                            <Row>
                                                <S.ServiceName>{service.name}</S.ServiceName>
                                            </Row>
                                            <Row>
                                                <S.ServiceDescription>{service.description}{service.duration ? ` · ${service.duration}` : ''}</S.ServiceDescription>
                                            </Row>
                                        </Column>

                                        <Row>
                                            <S.ServicePrice isVariable={isVariableService(service)}>{formatServicePrice(service)}</S.ServicePrice>
                                            <div>
                                                {
                                                    service.isSelected ?
                                                        <RadioBtnYES fill={COLORS.mainTeal} stroke={COLORS.mainTeal} /> :
                                                        <RadioBtnNO fill={'transparent'} stroke={COLORS.black1} />
                                                }
                                            </div>
                                        </Row>
                                    </S.ServiceItem>
                                ))}
                            </S.ServicesContainer>

                            <S.BookingSection>
                                <S.BookBox>
                                    <div>
                                        <S.SelectedServicesContainer totalItems={props.selectedServices.length}>
                                            {props.selectedServices.map(service => (
                                                <ItemPill key={service.id} service={service}
                                                    removeService={() => props.toggleSelectedService(service)} />
                                            ))}
                                        </S.SelectedServicesContainer>
                                    </div>
                                    <div>
                                        <S.BookBtn onClick={props.selectedServices.some(isVariableService) ? props.toggleRequestForm : props.toggleBookingFlow}>
                                            <p>
                                                {props.selectedServices.some(isVariableService) ? 'Solicitar presupuesto' : 'Ver Disponibilidad'}
                                            </p>
                                        </S.BookBtn>
                                        {!props.selectedServices.some(isVariableService) && (
                                            <S.WarningText>
                                                No será cobrado en este momento
                                            </S.WarningText>
                                        )}
                                    </div>

                                    <div>
                                        <S.BillingSummary>
                                            <p>Tratamientos</p>
                                            <p>{props.selectedServices.some(isVariableService) ? 'A consultar' : `$ ${props.totalPrice}`}</p>
                                        </S.BillingSummary>
                                    </div>
                                </S.BookBox>
                            </S.BookingSection>
                        </S.BottomSection>
                    )}

                </S.Content>

                {/* About section */}
                {props.selectedBusiness?.description && (
                    <S.AboutSection>
                        <S.SectionHeader>Sobre el negocio</S.SectionHeader>
                        <S.AboutText>{props.selectedBusiness?.description}</S.AboutText>
                    </S.AboutSection>
                )}

                {SHOW_REVIEWS && (
                    <>
                        <S.SeparatorLine />

                        <S.ReviewsSection>
                            <S.ReviewsFirstRow>
                                <Star height="25px" width="25px" />
                                <S.ScoreText>{calculateAverageStarRating(props.selectedBusiness?.starCount)}</S.ScoreText>
                                <p>·</p>
                                <S.ReviewsCommentsText>{totalReviews} reviews</S.ReviewsCommentsText>
                            </S.ReviewsFirstRow>

                            <S.ReviewsSecondRow>
                                <ReviewProgressItem id={'5'} bgColour={COLORS.mainTeal} reviews={calculatePercentageStarCount(props.selectedBusiness?.starCount?.[4] ?? 0, totalStarCount)} totalReviews={totalStarCount} />
                                <ReviewProgressItem id={'4'} bgColour={COLORS.mainTeal} reviews={calculatePercentageStarCount(props.selectedBusiness?.starCount?.[3] ?? 0, totalStarCount)} totalReviews={totalStarCount} />
                                <ReviewProgressItem id={'3'} bgColour={COLORS.mainTeal} reviews={calculatePercentageStarCount(props.selectedBusiness?.starCount?.[2] ?? 0, totalStarCount)} totalReviews={totalStarCount} />
                                <ReviewProgressItem id={'2'} bgColour={COLORS.mainTeal} reviews={calculatePercentageStarCount(props.selectedBusiness?.starCount?.[1] ?? 0, totalStarCount)} totalReviews={totalStarCount} />
                                <ReviewProgressItem id={'1'} bgColour={COLORS.mainTeal} reviews={calculatePercentageStarCount(props.selectedBusiness?.starCount?.[0] ?? 0, totalStarCount)} totalReviews={totalStarCount} />
                            </S.ReviewsSecondRow>

                            <CommentsSection comments={props.selectedBusiness?.reviewComments} businessTimezone={props.selectedBusiness?.timezone} />
                        </S.ReviewsSection>
                    </>
                )}
            </S.Container>

            <S.BottomBookBox>
                <S.BottomBarContent>
                    {isTattoo ? (
                        <S.BottomBookBtn fullWidth onClick={props.toggleRequestForm}>
                            <p>Reserva tu cita</p>
                        </S.BottomBookBtn>
                    ) : (
                        <>
                            <S.BottomBarServices>
                                {props.selectedServices.map(service => (
                                    <ItemPill
                                        key={service.id}
                                        service={service}
                                        removeService={() => props.toggleSelectedService(service)}
                                    />
                                ))}
                            </S.BottomBarServices>
                            <S.BottomBarPrice>{props.selectedServices.some(isVariableService) ? 'A consultar' : `$ ${props.totalPrice}`}</S.BottomBarPrice>
                            <S.BottomBookBtn onClick={props.selectedServices.some(isVariableService) ? props.toggleRequestForm : props.toggleBookingFlow}>
                                <p>{props.selectedServices.some(isVariableService) ? 'Solicitar presupuesto' : 'Ver Disponibilidad'}</p>
                            </S.BottomBookBtn>
                        </>
                    )}
                </S.BottomBarContent>
            </S.BottomBookBox>

                    </S.PageContainer>
                )}
            </>
        )
    };

export default BusinessDetailPresenter;
